/**
 * Gate accountability — required pace vs remaining calendar.
 * Deterministic. Evaluates only when executionAsOf is set (active sprint).
 */

import { deriveGateReadiness } from './gates';

export const GATE_ACCOUNTABILITY_SEVERITY = {
  ON_PACE: 'on_pace',
  AT_RISK: 'at_risk',
  WILL_MISS: 'will_miss',
  IMPOSSIBLE: 'impossible',
};

/** Tunable ladder constants (unit-tested). */
export const GATE_ACCOUNTABILITY_CONSTANTS = {
  /** Days-to-gate at or below which any missing required → will_miss. */
  willMissIfDaysAtMost: 3,
  /** Missing required contracts vs remaining work days → will_miss when missing > days. */
  missingVsDaysWillMiss: true,
  /** On gate day with remaining gaps — still will_miss until close; impossible only if day past gate. */
  impossibleOnlyAfterGateDay: true,
  /** Max miracle moves shown (Bridge45 mandate). */
  miraclePathCap: 3,
  /** Open Critical/High for next gate with few days → at_risk floor. */
  atRiskOpenCriticalWithDaysAtMost: 7,
};

function gateLabel(sprint, day) {
  const g = (sprint.gates || []).find((x) => Number(x.day) === Number(day));
  return g?.label || `Day ${day}`;
}

function miracleFrom(readiness, forNextGate) {
  const moves = [];
  const seen = new Set();
  for (const text of readiness.smallestActions || []) {
    const t = String(text || '').trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    moves.push({ text: t, why: 'Closes a missing or at-risk gate contract.', actionId: undefined });
    if (moves.length >= GATE_ACCOUNTABILITY_CONSTANTS.miraclePathCap) return moves;
  }
  const critical = (forNextGate || []).filter(
    (a) => a.priority === 'Critical' || a.priority === 'High',
  );
  for (const a of critical) {
    const t = String(a.text || '').trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    moves.push({
      text: t,
      why: `${a.priority} move tagged for the next gate.`,
      actionId: a.id,
    });
    if (moves.length >= GATE_ACCOUNTABILITY_CONSTANTS.miraclePathCap) break;
  }
  return moves;
}

function screamCopy(label) {
  return `If you don’t do this now, there is no realistic path to ${label}.`;
}

/**
 * @returns {{
 *   severity: 'on_pace'|'at_risk'|'will_miss'|'impossible',
 *   gateDay: number|null,
 *   gateLabel: string,
 *   daysRemainingToGate: number|null,
 *   missingRequiredCount: number,
 *   atRiskCount: number,
 *   scream: string|null,
 *   miraclePath: Array<{text:string, why:string, actionId?: string}>,
 *   math: object,
 *   quiet: boolean,
 * }}
 */
export function deriveGateAccountability(state, derived) {
  const sprint = derived?.sprint || {};
  const empty = {
    severity: GATE_ACCOUNTABILITY_SEVERITY.ON_PACE,
    gateDay: null,
    gateLabel: '',
    daysRemainingToGate: null,
    missingRequiredCount: 0,
    atRiskCount: 0,
    scream: null,
    miraclePath: [],
    math: {},
    quiet: true,
  };

  if (!sprint.executionAsOf || sprint.phase === 'pre') {
    return {
      ...empty,
      math: { reason: 'pre_sprint', note: 'Gate panic is suppressed until execution begins.' },
    };
  }

  const gateDay = Number(sprint.nextGate?.day || 0) || null;
  if (!gateDay) {
    return { ...empty, math: { reason: 'no_next_gate' } };
  }

  const readiness = deriveGateReadiness(state, derived, gateDay);
  const daysRemainingToGate =
    sprint.daysToGate != null
      ? Number(sprint.daysToGate)
      : sprint.calendarDaysToGate != null
        ? Number(sprint.calendarDaysToGate)
        : null;
  const missingRequiredCount = (readiness.missing || []).length;
  const atRiskCount = (readiness.atRisk || []).length;
  const label = gateLabel(sprint, gateDay);
  const readinessStatus = readiness.compact?.status || 'NOT ASSESSABLE';
  const forNextGate = derived.top3?.forNextGate || [];
  const openCriticalHigh = forNextGate.filter(
    (a) => a.priority === 'Critical' || a.priority === 'High',
  ).length;

  const math = {
    gateDay,
    daysRemainingToGate,
    missingRequiredCount,
    atRiskCount,
    openCriticalHighOnGate: openCriticalHigh,
    readinessStatus,
    assessable: readinessStatus !== 'NOT ASSESSABLE',
  };

  if (readinessStatus === 'NOT ASSESSABLE') {
    return {
      severity: GATE_ACCOUNTABILITY_SEVERITY.ON_PACE,
      gateDay,
      gateLabel: label,
      daysRemainingToGate,
      missingRequiredCount,
      atRiskCount,
      scream: null,
      miraclePath: [],
      math: { ...math, reason: 'not_assessable' },
      quiet: true,
    };
  }

  const C = GATE_ACCOUNTABILITY_CONSTANTS;
  let severity = GATE_ACCOUNTABILITY_SEVERITY.ON_PACE;

  const days = daysRemainingToGate == null ? 99 : daysRemainingToGate;
  const pastGate = days < 0;

  if (pastGate && missingRequiredCount > 0 && C.impossibleOnlyAfterGateDay) {
    severity = GATE_ACCOUNTABILITY_SEVERITY.IMPOSSIBLE;
  } else if (
    missingRequiredCount > 0 &&
    (days <= C.willMissIfDaysAtMost ||
      (C.missingVsDaysWillMiss && missingRequiredCount > Math.max(days, 0)))
  ) {
    severity = GATE_ACCOUNTABILITY_SEVERITY.WILL_MISS;
  } else if (
    readinessStatus === 'OFF TRACK' ||
    (missingRequiredCount > 0 && days <= C.atRiskOpenCriticalWithDaysAtMost) ||
    (atRiskCount > 0 && days <= C.atRiskOpenCriticalWithDaysAtMost) ||
    (openCriticalHigh > 0 && days <= C.atRiskOpenCriticalWithDaysAtMost && readinessStatus !== 'ON TRACK')
  ) {
    if (
      readinessStatus === 'OFF TRACK' &&
      missingRequiredCount > 0 &&
      days <= C.willMissIfDaysAtMost
    ) {
      severity = GATE_ACCOUNTABILITY_SEVERITY.WILL_MISS;
    } else if (readinessStatus === 'OFF TRACK' && missingRequiredCount > Math.max(days, 0)) {
      severity = GATE_ACCOUNTABILITY_SEVERITY.WILL_MISS;
    } else if (readinessStatus === 'ON TRACK' && atRiskCount === 0 && missingRequiredCount === 0) {
      severity = GATE_ACCOUNTABILITY_SEVERITY.ON_PACE;
    } else {
      severity = GATE_ACCOUNTABILITY_SEVERITY.AT_RISK;
    }
  } else if (readinessStatus === 'AT RISK' || atRiskCount > 0) {
    severity = GATE_ACCOUNTABILITY_SEVERITY.AT_RISK;
  } else if (readinessStatus === 'ON TRACK') {
    severity = GATE_ACCOUNTABILITY_SEVERITY.ON_PACE;
  }

  if (
    readinessStatus === 'OFF TRACK' &&
    missingRequiredCount > 0 &&
    days <= C.willMissIfDaysAtMost
  ) {
    severity = GATE_ACCOUNTABILITY_SEVERITY.WILL_MISS;
  }

  const screaming =
    severity === GATE_ACCOUNTABILITY_SEVERITY.WILL_MISS ||
    severity === GATE_ACCOUNTABILITY_SEVERITY.IMPOSSIBLE;

  return {
    severity,
    gateDay,
    gateLabel: label,
    daysRemainingToGate,
    missingRequiredCount,
    atRiskCount,
    scream: screaming ? screamCopy(label) : null,
    miraclePath: screaming ? miracleFrom(readiness, forNextGate) : [],
    math,
    quiet: severity === GATE_ACCOUNTABILITY_SEVERITY.ON_PACE,
  };
}
